import { toast } from '@/components/toast';
import { logger } from '@/services/logger';
import { useRef, useState } from 'react';

export const useRecording = (mediaStream: MediaStream | null, onChange: (blob: Blob | null) => void) => {
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [isPaused, setIsPaused] = useState<boolean>(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);

    const handleStartRecording = () => {
        try {
            if (mediaStream) {
                mediaRecorderRef.current = new MediaRecorder(mediaStream, { mimeType: 'video/mp4' });
                chunksRef.current = [];
                mediaRecorderRef.current.ondataavailable = event => {
                    if (event.data.size > 0) {
                        chunksRef.current.push(event.data);
                    }
                };

                mediaRecorderRef.current.start();
                setIsRecording(true);
                setIsPaused(false);
            }
        } catch (e) {
            toast.error({ title: 'Failed to start recording' });
            logger.error('Failed to start recording', e);
        }
    };

    const handlePauseRecording = () => {
        if (mediaRecorderRef.current && !isPaused) {
            mediaRecorderRef.current.pause();
            setIsPaused(true);
        } else {
            toast.error({ title: 'Cannot pause recording' });
        }
    };

    const handleResumeRecording = () => {
        if (mediaRecorderRef.current && isPaused) {
            mediaRecorderRef.current.resume();
            setIsPaused(false);
        } else {
            toast.error({ title: 'Cannot resume recording' });
        }
    };

    const handleFinishRecording = () => {
        return new Promise(resolve => {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.onstop = () => {
                    const completeBlob = new Blob(chunksRef.current, { type: 'video/mp4' });
                    onChange(completeBlob);
                    chunksRef.current = [];
                    resolve(null);
                };

                mediaRecorderRef.current.stop();
                setIsRecording(false);
                setIsPaused(false);

                mediaRecorderRef.current = null;
            } else {
                resolve(null);
            }
        });
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.ondataavailable = null;
            setIsRecording(false);
            setIsPaused(false);

            mediaRecorderRef.current = null;
        }
        onChange(null);
    };

    return {
        isRecording,
        isPaused,
        startRecording: handleStartRecording,
        pauseRecording: handlePauseRecording,
        resumeRecording: handleResumeRecording,
        finishRecording: handleFinishRecording,
        stopRecording: handleStopRecording,
    };
};
